import BehaviorConfig from '@/models/app-config/behavior/behavior'
import { ApplicationState } from '@/models/application-state/application-state'
import { ApplicationStateGateway, ErrorEvent, GatewayEvent } from '@/models/application-state/application-state-gateway'
import { PointFeatureIsEnabledService } from '@/services/api/point-feature'
import { SubscriptionService } from '@/services/api/subscription'
import { Either, left, right } from 'fp-ts/lib/Either'

export interface Dependencies {
  pointFeature: PointFeatureIsEnabledService,
  subscription: Pick<SubscriptionService, 'config'>,
  carat: {
    use: boolean
  },
  paidMembership: {
    enabled: boolean,
  }
}

export class ApplicationStateGatewayWhichCallEachApi implements ApplicationStateGateway {
  static setupDefault(): ApplicationStateGateway {
    return new ApplicationStateGatewayWhichCallEachApi({
      pointFeature: PointFeatureIsEnabledService.create(),
      subscription: new SubscriptionService(),
      carat: {
        use: new BehaviorConfig().useCarat,
      },
      paidMembership: {
        enabled: BehaviorConfig.forHost.paidMembership.enabled,
      },
    })
  }

  private readonly handler: Map<GatewayEvent, (e: any) => void> = new Map()

  constructor(private readonly deps: Dependencies) {
  }

  async fetch(): Promise<Either<Error[], ApplicationState>> {
    let errors: Error[] = []

    const handleError = (event: ErrorEvent) => (error: Error) => {
      this.emit(event, error)
      errors = [...errors, error]

      return false
    }
    const [point, subscription, carat, paidMembership] = await Promise.all([
      this.deps.pointFeature.isEnabled()
        .then(config => config.on)
        .catch(handleError('point-on-error')),
      this.deps.subscription.config().then(result => result.fold(
        handleError('subscription-on-error'),
        config => config.active
      )),
      Promise.resolve(this.deps.carat.use),
      Promise.resolve(this.deps.paidMembership.enabled),
    ])

    if (errors.length > 0) {
      return left(errors)
    }

    return right({
      point: {
        active: point,
      },
      subscription: {
        active: subscription,
      },
      carat: {
        active: carat,
      },
      paidMembership: {
        active: paidMembership,
      },
    })
  }

  on(event: ErrorEvent, callback: (e: Error) => void): ApplicationStateGateway {
    this.handler.set(event, callback)

    return this
  }
  private emit(event: ErrorEvent, e: Error): void {
    const h = this.handler.get(event)

    if (h !== undefined) h(e)
  }
}
